/**
 * Versioned wire contract — agent-runner `internal-v1` — for the SEPARATE,
 * machine-to-machine surface the Container calls back on
 * (`/api/agent-runner/internal/v1/*`), authenticated by a shared secret header
 * (`X-Agent-Container-Token`), never `ownerId`/Access (ADR 0065 §1). The public
 * `agent-v1` (`./http/agent-v1.ts`) stays owner/Access-facing; this one is only
 * ever reachable from inside our own trust boundary.
 *
 * Same discipline as every other contract: both sides parse the SAME Zod shapes
 * (ADR 0005). These schemas describe what the Python worker POSTs back as it runs
 * — its step-log events, lifecycle-status transitions, and artifact pointers.
 */
import { z } from 'zod';
import { AgentArtifactKindSchema, AgentRunEventKindSchema, AgentRunStatusSchema } from '../agent/domain.js';

export const AGENT_INTERNAL_HTTP_CONTRACT_VERSION = 'agent-internal-v1' as const;

/**
 * POST /api/agent-runner/internal/v1/runs/:id/events — the Container appends one
 * step-log event. `seq` is intentionally ABSENT: storage assigns the monotonic
 * per-run sequence (the same invariant `AgentRunnerStorage.appendEvent` enforces),
 * so a worker never guesses ordering.
 */
export const AgentEventAppendRequestSchema = z.object({
  kind: AgentRunEventKindSchema,
  /** Structural — shape varies per `kind` (a step's action/thought, a human-requested reason, …). */
  payload: z.record(z.unknown()),
  occurredAt: z.number().int().nonnegative(),
});
export type AgentEventAppendRequest = z.infer<typeof AgentEventAppendRequestSchema>;

/**
 * POST /api/agent-runner/internal/v1/runs/:id/status — the Container advances the
 * run's lifecycle status. `ownerId` is echoed by the (trusted) Container so the
 * write stays owner-scoped through the existing storage port without a lookup that
 * would break the owner-scoping invariant.
 */
export const AgentStatusUpdateRequestSchema = z.object({
  ownerId: z.string().min(1),
  status: AgentRunStatusSchema,
  startedAt: z.number().int().nonnegative().nullable().optional(),
  completedAt: z.number().int().nonnegative().nullable().optional(),
  errorMessage: z.string().max(2000).nullable().optional(),
});
export type AgentStatusUpdateRequest = z.infer<typeof AgentStatusUpdateRequestSchema>;

/**
 * POST /api/agent-runner/internal/v1/runs/:id/artifacts — the Container registers
 * an artifact pointer (a report/recording/screenshot). The bytes live in R2 /
 * Browser Run; this is metadata only (`AgentArtifactSchema`).
 */
export const AgentArtifactCreateRequestSchema = z.object({
  kind: AgentArtifactKindSchema,
  url: z.string().min(1),
});
export type AgentArtifactCreateRequest = z.infer<typeof AgentArtifactCreateRequestSchema>;

/**
 * POST /api/agent-runner/internal/v1/runs/:id/findings — the Container lands one
 * raw finding (§3.4). `ownerId` is echoed by the (trusted) Container so the row
 * is owner-scoped without a lookup; `id`/`capturedAt` are assigned by the API.
 * `schemaVersion` defaults to 1 when omitted.
 */
export const AgentFindingCreateRequestSchema = z.object({
  ownerId: z.string().min(1),
  kind: z.string().min(1).max(200),
  sourceUrl: z.string().max(4000).nullable().optional(),
  payload: z.record(z.unknown()),
  schemaVersion: z.number().int().positive().optional(),
});
export type AgentFindingCreateRequest = z.infer<typeof AgentFindingCreateRequestSchema>;

/**
 * PUT /api/agent-runner/internal/v1/sessions — the Container stores an encrypted
 * post-login session (ADR 0069). CIPHERTEXT pass-through: the API never sees the
 * plaintext `storage_state` — the Container seals it before it leaves (decision 4).
 * Upsert keyed by (ownerId, domain).
 */
export const AgentSiteSessionPutRequestSchema = z.object({
  ownerId: z.string().min(1),
  domain: z.string().min(1).max(4000),
  /** base64 Fernet token — ciphertext only, opaque to the API. */
  encryptedStorageState: z.string().min(1),
  encryptionKeyId: z.string().min(1).max(200),
});
export type AgentSiteSessionPutRequest = z.infer<typeof AgentSiteSessionPutRequestSchema>;

/**
 * GET /api/agent-runner/internal/v1/sessions?ownerId=&domain= → the sealed session
 * for reuse (the Container decrypts it inside). Ciphertext only; null when none.
 */
export const AgentSiteSessionGetResponseSchema = z.object({
  encryptedStorageState: z.string().min(1),
  encryptionKeyId: z.string().min(1).max(200),
});
export type AgentSiteSessionGetResponse = z.infer<typeof AgentSiteSessionGetResponseSchema>;

/**
 * POST /api/agent-runner/internal/v1/runs/:id/consume-message — the Container pops the next
 * queued follow-up for a live session (ADR 0078). Atomic: returns the pending message (if any)
 * and clears it, plus the run's current status so one call drives the whole wait loop — a
 * `prompt` means "run this next turn"; a terminal `status` (the operator closed the session)
 * means "exit". `ownerId` echoed by the trusted Container to stay owner-scoped.
 */
export const AgentConsumeMessageRequestSchema = z.object({ ownerId: z.string().min(1) });
export type AgentConsumeMessageRequest = z.infer<typeof AgentConsumeMessageRequestSchema>;

export const AgentConsumeMessageResponseSchema = z.object({
  status: AgentRunStatusSchema,
  /** The popped follow-up prompt, or null when nothing is queued. */
  prompt: z.string().min(1).max(4000).nullable(),
});
export type AgentConsumeMessageResponse = z.infer<typeof AgentConsumeMessageResponseSchema>;

/** Generic acknowledge payload for the internal write endpoints. */
export const AgentInternalAckSchema = z.object({ ok: z.literal(true) });
